/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
import React, { ChangeEvent, ReactNode, useRef, useState } from 'react';
import { useCombobox, useMultipleSelection } from 'downshift';
import classNames from 'classnames';
import { Icon, Label } from '@ver-uds/uswds-react';
import Chip from '../../Chip';
import { SearchSuggestion } from './SearchSuggestion';
import useMultiValueSearch from './useMultiValueSearch';
import ScreenReaderText from './ScreenReaderText';
import AutoGrowingInput from './AutoGrowingInput';

export interface MultiValueSearchProps {
  /** The label for the control */
  label?: ReactNode;
  /** Optional styling for the label */
  labelClassName?: string;
  /** Optional base id for all elements */
  id?: string;
  /** A list of potential search criteria displayed in dropdown */
  options?: SearchSuggestion[];
  /** Initially selected search criteria */
  initialValue?: SearchSuggestion[];
  /** Notifies subscribers when new search criteria selections are made */
  onChange?: (value: SearchSuggestion[] | undefined) => void;
  /** Placeholder text for input element */
  placeholder?: string;
  /** Allow duplicate selection of options */
  duplicateSelection?: boolean;
  /** Allows for custom assistive text */
  assistiveText?: string;
}

const noOp = () => {};

const defaultAssistiveText =
  'When search criteria are available use up and down arrows to review and enter to select. Touch device users, explore by touch or with swipe gestures.';

/**
 * Combines an input element and dropdown list to suggest search criteria based on input text and configured search criteria options.
 * Using keyboard or mouse, users can select multiple criteria from the dropdown that match the input text.
 */
const MultiValueSearch = ({
  label,
  labelClassName,
  placeholder,
  id,
  options: items = [],
  initialValue = [],
  onChange = noOp,
  duplicateSelection = false,
  assistiveText = defaultAssistiveText,
}: MultiValueSearchProps): JSX.Element => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [focused, setFocused] = useState(false);
  const assistiveHintID = id ? `${id}-assistiveHint` : 'ver-search__input-assistiveHint';
  const searchIconID = id ? `${id}-search-icon` : 'ver-search__search-icon';
  const {
    getSelectedItemProps,
    getDropdownProps,
    addSelectedItem,
    removeSelectedItem,
    selectedItems,
  } = useMultipleSelection<SearchSuggestion>({
    initialSelectedItems: initialValue,
    onSelectedItemsChange: (newSelection) => onChange(newSelection.selectedItems),
  });
  const { value, options: filteredSearchOptions, setValue } = useMultiValueSearch(
    items,
    selectedItems,
    duplicateSelection,
  );

  const {
    isOpen,
    openMenu,
    highlightedIndex,
    setHighlightedIndex,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    getItemProps,
  } = useCombobox<SearchSuggestion>({
    inputValue: value,
    items: filteredSearchOptions,
    id,
    stateReducer: (state, { changes }) => {
      return {
        ...changes,
        isOpen: changes.inputValue !== '' && changes.isOpen,
      };
    },
    onStateChange: ({ inputValue, type, selectedItem }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputChange:
          setValue(inputValue || '');
          break;
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputBlur:
          if (selectedItem) {
            setValue('');
            if (selectedItem.map) {
              const mappedItems = selectedItem.map(value);
              mappedItems
                .map((text) => ({
                  ...selectedItem,
                  text,
                }))
                .forEach(addSelectedItem);
            } else {
              addSelectedItem(selectedItem);
            }
          }
          break;
        default:
          break;
      }
    },
  });

  const onKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Enter':
      case 'Tab':
        if (isOpen && highlightedIndex === -1) {
          /* Focus (highlight) first item in menu. */
          setHighlightedIndex(0);
          event.preventDefault();
        }
        break;
      default:
    }
  };

  return (
    <div className="ver-search__menu-wrapper">
      <Label {...getLabelProps()} className={classNames('ver-search__label', labelClassName)}>
        {label}
      </Label>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        className={classNames('ver-search__container', {
          'ver-search__container--focused': focused,
          'ver-search__container--opened': isOpen,
        })}
        onClick={() => {
          inputRef.current?.focus();
          if (!isOpen && value) {
            // Open the menu when a value was previously typed.
            // This is needed for when you blur the input and refocus.
            openMenu();
          }
        }}
      >
        <Icon
          name="search"
          size={3}
          className="margin-top-1 margin-right-1"
          title={{ id: searchIconID, text: 'Search' }}
        />
        <div className="ver-search__control">
          {selectedItems.map((selectedItem: SearchSuggestion, index: number) => (
            <span
              // We have to use index here to support downshift's keyboard features.
              // eslint-disable-next-line react/no-array-index-key
              key={`selected-item-${index}`}
              className="ver-search__control-selection"
              {...getSelectedItemProps({
                selectedItem,
                index,
                onClick: (event) => {
                  // Prevent `inputRef.current?.focus()` from being called when clicking on a Chip
                  // Allow event when the close button is clicked.
                  if (['SPAN'].includes((event.target as HTMLElement).tagName?.toUpperCase())) {
                    event.stopPropagation();
                  }
                },
              })}
            >
              <Chip
                ariaLabel={`Remove ${selectedItem.label}: ${selectedItem.text?.formatted}`}
                onClick={(event) => {
                  if (selectedItems.length > 1) {
                    event.stopPropagation();
                  }
                  removeSelectedItem(selectedItem);
                }}
              >
                {`${selectedItem.label}: ${selectedItem.text?.formatted}`}
              </Chip>
            </span>
          ))}
          <div {...getComboboxProps()} className="ver-search__combobox">
            <AutoGrowingInput
              {...getInputProps(
                getDropdownProps({
                  /* Can't prevent key action because we want to manage when menu items are focused. */
                  preventKeyAction: false,
                  onKeyDown,
                  placeholder,
                  ref: inputRef,
                  'aria-describedby': assistiveHintID,
                  onFocus: () => setFocused(true),
                  onBlur: () => setFocused(false),
                  // Ensure the new value is set in the first render to prevent this bug:
                  // https://git.uscis.dhs.gov/USCIS/ver-uds/issues/577
                  onChange: (event: ChangeEvent<HTMLInputElement>) => setValue(event.target.value),
                }),
              )}
              aria-labelledby={null}
            />
          </div>
        </div>
      </div>
      <ul
        {...getMenuProps()}
        className={classNames('ver-search__menu', {
          'ver-search__menu--opened': isOpen,
        })}
      >
        {isOpen &&
          filteredSearchOptions.map((item: SearchSuggestion, index: number) => (
            <li
              key={item.value}
              {...getItemProps({ item, index })}
              className={classNames('ver-search__option', {
                'ver-search__option--focused': index === highlightedIndex,
              })}
            >
              <div className="ver-search__icon">
                <Icon name="search" title={{ id: `${item.value}-icon`, text: 'Search' }} />
              </div>
              <span className="ver-search__option-text">
                <b>{item.label}:</b> {item.text?.formatted}
              </span>
            </li>
          ))}
      </ul>

      {isOpen && filteredSearchOptions.length === 0 && (
        <div
          className={classNames('ver-search__menu', {
            'ver-search__menu--opened': isOpen,
          })}
        >
          <div className="ver-search__option">
            <span className="ver-search__option-text" role="alert">
              No matching criteria.
            </span>
          </div>
        </div>
      )}

      <ScreenReaderText
        showList={isOpen}
        filteredOptionsLength={filteredSearchOptions.length}
        assistiveHintID={assistiveHintID}
        assistiveText={assistiveText}
      />
    </div>
  );
};

export default MultiValueSearch;
